import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import { useTasks } from '../../hooks/useTasks';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import { downloadInvoice } from '../../utils/invoiceDownload';
import { shouldAllowNotification } from '../../constants/notificationPreferences';
import HelpIcon from '../HelpIcon.jsx';
import { isProUser, canAccessChat, canAccessProFeatures } from '../../utils/subscriptionHelpers';
import { determineProAccess } from '../../utils/proAccess';
import { ReceevitLogoCompact } from '../common/ReceevitLogo';
import InvoicePreview from '../shared/InvoicePreview';
import PlanBadge from '../common/PlanBadge';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import {
  LayoutDashboard,
  FileText,
  Download,
  Upload,
  MessageSquare,
  CreditCard,
  Settings,
  Menu,
  X,
  LogOut,
  User,
  Bell,
  HelpCircle,
  X as CloseIcon,
  Lock,
  CheckSquare,
  Users,
  Search,
  Plus,
  ArrowRight,
  AlertTriangle,
  Link as LinkIcon
} from 'lucide-react';

const INACTIVE_ALLOWED_PATHS = ['/portal/settings'];

const PortalLayout = () => {
  const { user, logout, notificationPreferences } = useAuth();
  const navigate = useNavigate();
  const { openPreferences } = useCookieConsent();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { unreadCount: unreadMessages, refreshUnreadCount, markChatAsRead, markChatAsVisited } = useUnreadCount();
  const { badgeCount: todoCount } = useTasks();
  const [twoFactorStatus, setTwoFactorStatus] = useState({ loading: true, reconfigure: false, data: null });
  
  // New state for enhanced features
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const [plusDropdownOpen, setPlusDropdownOpen] = useState(false);
  const [helpDropdownOpen, setHelpDropdownOpen] = useState(false);
  
  // Notification preview modal state
  const [notificationPreviewModal, setNotificationPreviewModal] = useState({
    isOpen: false,
    notification: null
  });
  
  // Invoice preview modal state (for "View Full Invoice" from notifications)
  const [invoicePreviewModal, setInvoicePreviewModal] = useState({
    isOpen: false,
    invoice: null,
    loading: false
  });
  
  // Debug unread count
  useEffect(() => {
    // PortalLayout unreadMessages state updated
  }, [unreadMessages]);

  // Update subscription when user changes
  useEffect(() => {
    setUserSubscription(user?.subscriptionPlan || 'Essential');
  }, [user?.subscriptionPlan]);
  const [userSubscription, setUserSubscription] = useState(user?.subscriptionPlan || 'Essential'); // Use user's actual subscription plan
  const [notifications, setNotifications] = useState([]);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const location = useLocation();
  const normalizedUserStatus = String(user?.status || '').toLowerCase();
  const normalizedSubscriptionStatus = String(user?.subscriptionStatus || '').toLowerCase();
  const isInactiveAccount =
    normalizedUserStatus === 'inactive' ||
    normalizedSubscriptionStatus === 'cancelled';
  const allowedInactivePaths = INACTIVE_ALLOWED_PATHS;

  // Keyboard shortcut for tour
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'h') {
        event.preventDefault();
        // Trigger tour by simulating click on help icon
        const helpIcon = document.querySelector('[data-help-icon]');
        if (helpIcon) {
          helpIcon.click();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);



  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationDropdownOpen && !event.target.closest('.notification-dropdown')) {
        setNotificationDropdownOpen(false);
      }
      if (searchDropdownOpen && !event.target.closest('.search-dropdown')) {
        setSearchDropdownOpen(false);
      }
      if (plusDropdownOpen && !event.target.closest('.plus-dropdown')) {
        setPlusDropdownOpen(false);
      }
      if (helpDropdownOpen && !event.target.closest('.help-dropdown')) {
        setHelpDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationDropdownOpen, searchDropdownOpen, plusDropdownOpen, helpDropdownOpen]);

  useEffect(() => {
    if (!user || user.role === 'additional_user') {
      setTwoFactorStatus({ loading: false, reconfigure: false, data: null });
      return;
    }
    let cancelled = false;

    const loadTwoFactorStatus = async () => {
      try {
        const response = await api.get('/users/security', { __skipAuthTokenPurge: true });
        if (cancelled) {
          return;
        }
        if (response.data.success) {
          const twoFactor = response.data.data?.twoFactor || {};
          const reconfigureRequired = Boolean(twoFactor.reconfigureRequired);
          const hasCompletedInitialSetup = Boolean(twoFactor.hasCompletedInitialSetup);
          const daysRemaining = typeof twoFactor.daysRemaining === 'number' ? twoFactor.daysRemaining : null;
          const needsSetup = twoFactor.enabled !== true;
          let graceExpired = false;
          if (typeof daysRemaining === 'number') {
            graceExpired = daysRemaining <= 0;
          } else if (hasCompletedInitialSetup) {
            graceExpired = true;
          }
          const requireImmediate = reconfigureRequired || (needsSetup && graceExpired);
          setTwoFactorStatus({
            loading: false,
            reconfigure: requireImmediate,
            data: twoFactor
          });
        } else {
          setTwoFactorStatus({ loading: false, reconfigure: false, data: null });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('PortalLayout: failed to load two-factor status', error);
          setTwoFactorStatus(prev => ({ ...prev, loading: false }));
        }
      }
    };

    loadTwoFactorStatus();
    const interval = setInterval(loadTwoFactorStatus, 30000);
    const listener = () => loadTwoFactorStatus();
    window.addEventListener('twofactor:updated', listener);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('twofactor:updated', listener);
    };
  }, [user?.id]);

  useEffect(() => {
    if (
      !twoFactorStatus.loading &&
      twoFactorStatus.reconfigure &&
      user &&
      user.role !== 'additional_user' &&
      !location.pathname.startsWith('/portal/settings')
    ) {
      localStorage.setItem('settings_active_tab', 'security');
      navigate('/portal/settings', { replace: true });
    }
  }, [twoFactorStatus.loading, twoFactorStatus.reconfigure, navigate, user, location.pathname]);

  useEffect(() => {
    if (isInactiveAccount) {
      const isAllowedPath = allowedInactivePaths.some(path =>
        location.pathname === path || location.pathname.startsWith(`${path}/`)
      );
      if (!isAllowedPath) {
        try {
          localStorage.setItem('settings_active_tab', 'subscription');
        } catch (error) {
          console.debug('Unable to set settings_active_tab in localStorage:', error);
        }
        navigate('/portal/settings?tab=subscription', { replace: true });
      }
      if (plusDropdownOpen) {
        setPlusDropdownOpen(false);
      }
      if (searchDropdownOpen) {
        setSearchDropdownOpen(false);
      }
      if (notificationDropdownOpen) {
        setNotificationDropdownOpen(false);
      }
    }
  }, [isInactiveAccount, allowedInactivePaths, location.pathname, navigate, plusDropdownOpen, searchDropdownOpen, notificationDropdownOpen]);

  const handleGoToSecurity = () => {
    localStorage.setItem('settings_active_tab', 'security');
    if (!location.pathname.startsWith('/portal/settings')) {
      navigate('/portal/settings');
    }
  };

  const handleInvoiceDownload = async (format, invoice, extraOptions = {}) => {
    try {
      await downloadInvoice(
        invoice,
        format,
        { ...extraOptions }
      );
    } catch (error) {
      console.warn('Notification invoice download failed, using legacy renderer', error);
      try {
        await legacyNotificationDownload(format, invoice);
      } catch (legacyError) {
        console.error('Error downloading invoice:', legacyError);
        alert('Error downloading invoice. Please try again.');
      }
    }
  };

  const showTwoFactorOverlay =
    !twoFactorStatus.loading &&
    twoFactorStatus.reconfigure &&
    !location.pathname.startsWith('/portal/settings');

  const twoFactorDetails = twoFactorStatus.data || {};

  const renderTwoFactorOverlay = showTwoFactorOverlay ? (
    <div className="fixed inset-0 z-[9998] bg-white/95 backdrop-blur-sm flex items-center justify-center px-6 text-center">
      <div className="max-w-lg w-full bg-white border border-indigo-100 shadow-xl rounded-2xl p-8 space-y-4">
        <div className="mx-auto w-14 h-14 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <Lock className="w-7 h-7" />
        </div>
        {(() => {
          const isReconfigure = Boolean(twoFactorDetails?.reconfigureRequired);
          return (
            <>
              <h2 className="text-xl font-semibold text-indigo-900">
                {isReconfigure
                  ? 'Reconfigure two-factor authentication to continue'
                  : 'Set up two-factor authentication to continue'}
              </h2>
              <p className="text-sm text-indigo-800">
                {isReconfigure
                  ? 'For your security, you must reconfigure your authenticator app before you can keep using Receevit®.'
                  : 'Your grace period has ended. Complete two-factor authentication now to keep your Receevit® account protected.'}
              </p>
            </>
          );
        })()}
        {Boolean(twoFactorDetails?.firstVerifiedAt) && (
          <p className="text-xs text-indigo-600">
            Last verified on {new Date(twoFactorDetails.firstVerifiedAt).toLocaleString()}
          </p>
        )}
        <div className="space-y-3">
          <button
            onClick={handleGoToSecurity}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow transition"
          >
            Reconfigure now
            <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-indigo-700">
            Access to the dashboard and other features is temporarily locked until reconfiguration is complete.
          </p>
        </div>
      </div>
    </div>
  ) : null;

  // Global search functionality
  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearchDropdownOpen(false);
      return;
    }

    try {
      const results = [];
      
      // Search contacts
      try {
        const contactsResponse = await api.get(`/contacts?search=${encodeURIComponent(query)}`);
        if (contactsResponse.data.success && contactsResponse.data.data?.contacts) {
          contactsResponse.data.data.contacts.forEach(contact => {
            results.push({
              id: contact.id,
              type: 'contact',
              title: contact.name || `${contact.firstName} ${contact.lastName}`,
              subtitle: contact.email || contact.company,
              route: `/portal/contacts?highlight=${contact.id}`,
              icon: Users
            });
          });
        }
      } catch (error) {
        console.log('Contacts search error:', error);
      }

      // Search sent invoices
      try {
        const sentInvoicesResponse = await api.get(`/invoices?role=sender&search=${encodeURIComponent(query)}`);
        if (sentInvoicesResponse.data.success && sentInvoicesResponse.data.data?.invoices) {
          sentInvoicesResponse.data.data.invoices.forEach(invoice => {
            // Handle guest invoices vs regular invoices
            let recipientName = 'Unknown';
            if (invoice.receiver) {
              // Regular invoice with registered user
              recipientName = invoice.receiver.companyName || 
                `${invoice.receiver.firstName || ''} ${invoice.receiver.lastName || ''}`.trim();
            } else if (invoice.guestRecipient) {
              // Guest invoice
              const guest = typeof invoice.guestRecipient === 'string' 
                ? JSON.parse(invoice.guestRecipient) 
                : invoice.guestRecipient;
              recipientName = guest.companyName || 
                `${guest.firstName || ''} ${guest.lastName || ''}`.trim() ||
                guest.email ||
                'Guest';
            }
            
            results.push({
              id: invoice.id,
              type: 'sent-invoice',
              title: invoice.invoiceNumber,
              subtitle: `To: ${recipientName}`,
              route: `/portal/invoices/sent?highlight=${invoice.id}`,
              icon: FileText
            });
          });
        }
      } catch (error) {
        console.log('Sent invoices search error:', error);
      }

      // Search received invoices
      try {
        const receivedInvoicesResponse = await api.get(`/invoices?role=receiver&search=${encodeURIComponent(query)}`);
        if (receivedInvoicesResponse.data.success && receivedInvoicesResponse.data.data?.invoices) {
          receivedInvoicesResponse.data.data.invoices.forEach(invoice => {
            // Received invoices should always have a sender, but add safety check
            let senderName = 'Unknown';
            if (invoice.sender) {
              senderName = invoice.sender.companyName || 
                `${invoice.sender.firstName || ''} ${invoice.sender.lastName || ''}`.trim() ||
                'Unknown';
            }
            
            results.push({
              id: invoice.id,
              type: 'received-invoice',
              title: invoice.invoiceNumber,
              subtitle: `From: ${senderName}`,
              route: `/portal/invoices/received?highlight=${invoice.id}`,
              icon: FileText
            });
          });
        }
      } catch (error) {
        console.log('Received invoices search error:', error);
      }

      // Search tasks
      try {
        const tasksResponse = await api.get(`/tasks?search=${encodeURIComponent(query)}`);
        if (tasksResponse.data.success && (tasksResponse.data.tasks || tasksResponse.data.data?.tasks)) {
          const tasks = tasksResponse.data.tasks || tasksResponse.data.data.tasks || [];
          tasks.forEach(task => {
            results.push({
              id: task.id,
              type: 'task',
              title: task.title,
              subtitle: task.description || `Status: ${task.status}`,
              route: `/portal/todo?highlight=${task.id}`,
              icon: CheckSquare
            });
          });
        }
      } catch (error) {
        console.log('Tasks search error:', error);
      }

      // Search payments
      try {
        const paymentsResponse = await api.get(`/invoices/paid?search=${encodeURIComponent(query)}`);
        if (paymentsResponse.data.success && paymentsResponse.data.data?.invoices) {
          paymentsResponse.data.data.invoices.forEach(payment => {
            results.push({
              id: payment.id,
              type: 'payment',
              title: payment.invoiceNumber,
              subtitle: `Paid: ${payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : 'Unknown'}`,
              route: `/portal/payments?highlight=${payment.id}`,
              icon: CreditCard
            });
          });
        }
      } catch (error) {
        console.log('Payments search error:', error);
      }

      setSearchResults(results.slice(0, 10)); // Limit to 10 results
      setSearchDropdownOpen(results.length > 0);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setSearchDropdownOpen(false);
    }
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    handleSearch(query);
  };

  // Handle search result click
  const handleSearchResultClick = (result) => {
    navigate(result.route);
    setSearchDropdownOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Handle plus button actions
  const handlePlusAction = (action) => {
    setPlusDropdownOpen(false);
    switch (action) {
      case 'invoice':
        navigate('/portal/send-invoice');
        break;
      case 'contact':
        navigate('/portal/contacts?action=add');
        break;
      case 'task':
        navigate('/portal/todo?action=add');
        break;
      default:
        break;
    }
  };

  // Unread count is now handled by the useUnreadCount hook

  // Fetch notifications from backend
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('📱 No token found, skipping notifications fetch');
          return;
        }

        console.log('📱 Fetching notifications...');
        const response = await api.get('/api/notifications');
        console.log('📱 Notifications response:', response.data);
        const allNotifications = response.data.notifications || [];
        const filteredNotifications = allNotifications.filter((notification) =>
          shouldAllowNotification(notificationPreferences, notification.type)
        );
        setNotifications(filteredNotifications);
        const unreadCount = filteredNotifications.filter((notification) => !notification.read && !notification.isRead).length;
        setUnreadNotifications(unreadCount);
      } catch (error) {
        console.error('📱 Failed to fetch notifications:', error);
        // Don't crash the component if notifications fail
        setNotifications([]);
        setUnreadNotifications(0);
      }
    };

    fetchNotifications();
    
    // Refresh notifications every 10 seconds for real-time updates
    const interval = setInterval(fetchNotifications, 10 * 1000);
    
    return () => clearInterval(interval);
  }, [notificationPreferences]);

  useEffect(() => {
    setNotifications((prev) => {
      const filtered = prev.filter((notification) => shouldAllowNotification(notificationPreferences, notification.type));
      setUnreadNotifications(filtered.filter((notification) => !notification.read && !notification.isRead).length);
      return filtered;
    });
  }, [notificationPreferences]);


  // Handle notification actions
  const handleNotificationClick = async (notification) => {
    try {
      console.log('📱 Notification clicked:', notification);
      
      // Mark as read - use POST endpoint with notificationId in body
      const token = localStorage.getItem('token');
      if (token && notification.id) {
        await api.post('/api/notifications/mark-read', { notificationId: notification.id });
      }

      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
      );
      setUnreadNotifications(prev => Math.max(0, prev - 1));

      // Close notification dropdown
      setNotificationDropdownOpen(false);

      // Show notification preview modal instead of navigating directly
      setNotificationPreviewModal({
        isOpen: true,
        notification: notification
      });

    } catch (error) {
      console.error('📱 Error handling notification:', error);
    }
  };

  // Handle "View Full Invoice" from notification preview modal
  const handleViewFullInvoice = async (notification) => {
    // Close the notification preview modal
    setNotificationPreviewModal({ isOpen: false, notification: null });
    
    // Show loading state
    setInvoicePreviewModal({ isOpen: true, invoice: null, loading: true });

    try {
      const invoiceId = notification.metadata?.invoiceId;
      if (!invoiceId) {
        console.error('❌ No invoice ID in notification metadata');
        setInvoicePreviewModal({ isOpen: false, invoice: null, loading: false });
        return;
      }

      console.log('📄 Fetching full invoice details for:', invoiceId);
      
      // Fetch the full invoice details
      const response = await api.get(`/invoices/${invoiceId}`);
      console.log('🔍 PortalLayout notification - API response:', response.data);
      
      // API returns: { success: true, data: { invoice } }
      const invoice = response.data?.data?.invoice || response.data?.invoice || response.data?.data;
      console.log('🔍 PortalLayout notification - Extracted invoice:', invoice);
      console.log('🔍 PortalLayout notification - Line items:', invoice?.items || invoice?.lineItems);
      console.log('🔍 PortalLayout notification - Total:', invoice?.total);
      console.log('🔍 PortalLayout notification - Amount:', invoice?.amount);
      
      if (invoice) {
        console.log('✅ Invoice fetched successfully:', invoice.invoiceNumber);
        
        // Ensure line items are properly mapped
        const completeInvoice = {
          ...invoice,
          lineItems: invoice.items || invoice.lineItems || [],
          amount: invoice.total || invoice.amount
        };
        
        console.log('🔍 PortalLayout notification - Complete invoice:', {
          invoiceNumber: completeInvoice.invoiceNumber,
          lineItemsCount: completeInvoice.lineItems?.length,
          amount: completeInvoice.amount,
          total: completeInvoice.total
        });
        
        setInvoicePreviewModal({ 
          isOpen: true, 
          invoice: completeInvoice, 
          loading: false 
        });
      } else {
        console.error('❌ No invoice data in response:', response.data);
        setInvoicePreviewModal({ isOpen: false, invoice: null, loading: false });
      }
    } catch (error) {
      console.error('❌ Failed to fetch invoice:', error);
      alert('Failed to load invoice details. Please try again.');
      setInvoicePreviewModal({ isOpen: false, invoice: null, loading: false });
    }
  };

  const legacyNotificationDownload = async (format, invoice) => {
    try {
      if (format === 'pdf') {
        // Generate PDF using browser print functionality
        // Note: User should disable headers/footers in print dialog for cleanest output
        setTimeout(() => {
          window.print();
        }, 100); // Small delay to ensure modal is ready
      } else if (format === 'csv') {
        // Generate CSV for line items
        let csv = 'Description,Quantity,Unit Price,Tax %,Discount %,Total\n';
        
        (invoice.lineItems || []).forEach(item => {
          const total = parseFloat(item.total || 0);
          csv += `"${item.description}",${item.quantity},£${item.price},${item.taxRate || 0}%,${item.discountPercent || 0}%,£${total.toFixed(2)}\n`;
        });
        
        // Add summary
        csv += '\n';
        csv += `Subtotal,,,,£${parseFloat(invoice.subtotal || 0).toFixed(2)}\n`;
        csv += `Tax,,,,£${parseFloat(invoice.tax || 0).toFixed(2)}\n`;
        if (invoice.discountAmount && parseFloat(invoice.discountAmount) > 0) {
          csv += `Early Payment Discount,,,,£-${parseFloat(invoice.discountAmount).toFixed(2)}\n`;
        }
        csv += `Total,,,,£${parseFloat(invoice.amount || invoice.total || 0).toFixed(2)}\n`;
        
        // Download CSV
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${invoice.invoiceNumber || 'invoice'}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Error downloading invoice. Please try again.');
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await api.put('/api/notifications/mark-all-read');

      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadNotifications(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      await api.delete('/notifications/clear-all');

      setNotifications([]);
      setUnreadNotifications(0);
      setNotificationDropdownOpen(false);
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };



  // Only fetch subscription when user.id changes, not on every render
  const userId = user?.id;
  const lastUserIdRef = useRef(null);
  
  const fetchUserSubscription = useCallback(async () => {
    try {
      const response = await api.get('/users/profile');
      const data = response.data;
      const plan = data.subscriptionPlan;

      if (plan) {
        let frontendPlan;
        switch (plan) {
          case 'pro':
            frontendPlan = 'Pro';
            break;
          case 'essential':
            frontendPlan = 'Essential';
            break;
          case 'free_trial':
            frontendPlan = 'Free Trial';
            break;
          default:
            frontendPlan = 'Essential';
        }

        setUserSubscription(frontendPlan);
      } else {
        setUserSubscription('Essential');
      }
    } catch (error) {
      console.error('Failed to fetch user subscription:', error);
      setUserSubscription('Essential');
    }
  }, []); // No dependencies - stable function

  // Only fetch when userId actually changes (login/logout)
  useEffect(() => {
    if (userId && userId !== lastUserIdRef.current) {
      lastUserIdRef.current = userId;
      fetchUserSubscription();
    }
  }, [userId, fetchUserSubscription]);

  // Listen for subscription change events
  useEffect(() => {
    const handleSubscriptionChange = () => {
      fetchUserSubscription();
    };

    window.addEventListener('subscriptionChanged', handleSubscriptionChange);

    return () => {
      window.removeEventListener('subscriptionChanged', handleSubscriptionChange);
    };
  }, [fetchUserSubscription]);



  // Function to test different subscription plans (for development)
  // Note: This is now handled by the backend API call
  const testSubscriptionPlan = (plan) => {
    
  };



  // Handle notification action (after viewing in modal)
  const handleNotificationAction = (notification) => {
    if (notification.type === 'chat' && notification.requiresPro && userSubscription !== 'Pro') {
      // Show upgrade prompt for Chat notifications for Essential users
      const message = `Chat is available on Pro plan. Upgrade to read your ${notification.unreadCount || unreadMessages} unread message${(notification.unreadCount || unreadMessages) !== 1 ? 's' : ''}.`;
      alert(message); // Replace with proper modal in production
      return;
    }

    // Handle different notification types for Pro users
    switch (notification.type) {
      case 'chat':
        // Navigate to chat for Pro users
        if (userSubscription === 'Pro') {
          // TODO: Navigate to chat page
          // Navigating to chat
        }
        break;
      case 'invoice':
        // TODO: Navigate to invoice details
        // Navigating to invoice
        break;
      case 'payment':
        // TODO: Navigate to payment details
        // Navigating to payment
        break;
      default:
        break;
    }

    // Close modal after action
    setShowNotificationModal(false);
    setSelectedNotification(null);
  };



  // Clear individual notification
  const clearNotification = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadNotifications(prev => Math.max(0, prev - 1));
      
      // Close modal if it was the selected notification
      if (selectedNotification && selectedNotification.id === notificationId) {
        setShowNotificationModal(false);
        setSelectedNotification(null);
      }
    } catch (error) {
      console.error('Error clearing notification:', error);
    }
  };



  // Get unread notification count
  const computedUnreadNotifications = notifications.filter(n => !n.read).length;

  // Helper functions
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'chat':
        return <MessageSquare className="w-5 h-5" />;
      case 'invoice':
        return <FileText className="w-5 h-5" />;
      case 'payment':
        return <CreditCard className="w-5 h-5" />;
      case 'system':
        return <Bell className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'chat':
        return 'text-blue-600 bg-blue-50';
      case 'invoice':
        return 'text-green-600 bg-green-50';
      case 'payment':
        return 'text-purple-600 bg-purple-50';
      case 'system':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const navigation = [
    { name: 'Dashboard', href: '/portal/dashboard', icon: LayoutDashboard, requiresPro: false },
    { name: 'Send Invoice', href: '/portal/send-invoice', icon: FileText, requiresPro: false },
    { name: 'Received Invoices', href: '/portal/invoices/received', icon: Download, requiresPro: false },
    { name: 'Sent Invoices', href: '/portal/invoices/sent', icon: Upload, requiresPro: false },
    { name: 'Chat', href: '/portal/chat', icon: MessageSquare, requiresPro: true, badge: unreadMessages },
    { name: 'Payments', href: '/portal/payments', icon: CreditCard, requiresPro: false },
    { name: 'To-Do', href: '/portal/todo', icon: CheckSquare, requiresPro: false },
    { name: 'Contacts', href: '/portal/contacts', icon: Users, requiresPro: false },
    { name: 'Integrations', href: '/portal/integrations', icon: LinkIcon, requiresPro: false },
    // Settings tab visible to all users (additional users see limited Account Info only)
    { name: 'Settings', href: '/portal/settings', icon: Settings, requiresPro: false },
  ];
  const navigationItems = isInactiveAccount
    ? navigation.filter(item => item.name === 'Settings')
    : navigation;

  const handleLogout = () => {
    logout();
  };


  const handleChatClick = () => {
    // Mark chat as visited (clears unread count badge)
    markChatAsVisited();
    setSidebarOpen(false);
    
    // Prevent scroll when navigating to chat
    setTimeout(() => {
      window.scrollTo(0, 0);
      const mainContent = document.querySelector('main');
      if (mainContent) {
        mainContent.scrollTop = 0;
      }
    }, 100);
  };



  // Handle restricted access for Pro-only features
  const handleRestrictedAccess = (featureName) => {
    let message;
    if (featureName === 'Chat') {
      message = `Chat is available on Pro plan. Upgrade to access your ${unreadMessages} unread message${unreadMessages !== 1 ? 's' : ''}.`;
    } else {
      message = `${featureName} is available on Pro plan only. Upgrade to access.`;
    }
    
    // Access restricted
    
    // TODO: Show upgrade modal or redirect to upgrade page
    // For now, we'll just log the message
    alert(message); // Replace with proper modal in production
  };


  return (
    <>
      {renderTwoFactorOverlay}
    <div className="flex h-screen bg-white">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75"></div>
        </div>
      )}


              {/* Sidebar */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 overflow-hidden shadow-lg
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          {/* Sidebar Header - Blue background like landing page */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 brand-blue relative z-10">
          <div className="flex items-center">
            <ReceevitLogoCompact size={160} forBlueBackground={true} />
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 text-white hover:bg-blue-700 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info - White background */}
        <div className="px-6 py-4 border-b border-gray-200 relative z-10 bg-white">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 brand-blue-active rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'User'}
                </p>
                {/* User's Own Plan Badge */}
                <PlanBadge plan={user?.subscription?.plan || user?.subscriptionPlan} />
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2 relative z-10">
          {navigationItems.map((item) => {
            const itemHref = item.href;
            const targetHref =
              isInactiveAccount && item.name === 'Settings'
                ? '/portal/settings?tab=subscription'
                : itemHref;
            const isActive =
              location.pathname === itemHref ||
              location.pathname.startsWith(`${itemHref}/`);
            const isChat = item.name === 'Chat';
            const hasProAccess = determineProAccess(user);
            const isRestricted = item.requiresPro && !hasProAccess;
            const canAccess = !item.requiresPro || hasProAccess;
            
            // Debug chat item - only log on mount or when access actually changes
            // Removed excessive logging to prevent console spam
            
            return (
              <div key={item.name} className="relative">
                {canAccess ? (
                  <Link
                      to={targetHref}
                    className={`
                      relative flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 group
                      ${isActive
                        ? 'brand-blue-active text-white shadow-md'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                      }
                    `}
                    onClick={() => {
                      if (isInactiveAccount && item.name === 'Settings') {
                        try {
                          localStorage.setItem('settings_active_tab', 'subscription');
                        } catch (error) {
                          console.debug('Unable to set settings_active_tab in localStorage:', error);
                        }
                        navigate('/portal/settings?tab=subscription');
                        setSidebarOpen(false);
                        return;
                      }
                      if (isChat) {
                        handleChatClick();
                        return;
                      }
                      setSidebarOpen(false);
                    }}
                  >
                    <item.icon className={`w-5 h-5 transition-all duration-300 ${
                      isActive ? 'text-white' : 'text-gray-600 group-hover:text-gray-900'
                    }`} />
                    <span className={`transition-all duration-300 ${
                      isActive ? 'font-semibold' : 'font-medium'
                    }`}>
                      {item.name}
                    </span>
                    
                    {/* Badge for Chat and To-Do */}
                    {item.badge > 0 && (
                      <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                        {item.badge > 9 ? '9+' : item.badge}
                      </div>
                    )}
                    {/* Debug info for chat badge */}
                    
                    {/* Active State Indicator */}
                    {isActive && (
                      <div className="absolute right-2 w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    )}
                  </Link>
                ) : (
                  <button
                    onClick={() => handleRestrictedAccess(item.name)}
                    className="relative flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 group w-full text-left cursor-pointer"
                    title={`${item.name} is available on Pro plan only. Upgrade to access.`}
                  >
                    <item.icon className="w-5 h-5 text-gray-400 group-hover:text-gray-500 transition-all duration-300" />
                    <span className="text-gray-400 group-hover:text-gray-500 transition-all duration-300">
                      {item.name}
                    </span>
                    
                    {/* Unread Messages Badge for Chat (even for restricted users) */}
                    {isChat && unreadMessages > 0 && (
                      <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                        {unreadMessages > 9 ? '9+' : unreadMessages}
                      </div>
                    )}
                    {/* Debug info for restricted chat badge */}
                    
                    {/* Lock Icon and Pro Badge */}
                    <div className="flex items-center space-x-2 ml-auto">
                      <Lock className="w-4 h-4 text-gray-400 group-hover:text-gray-500 transition-all duration-300" />
                      <div className="bg-yellow-100 text-yellow-700 text-xs font-bold rounded-full px-2 py-1 border border-yellow-300">
                        PRO
                      </div>
                    </div>
                  </button>
                )}
              </div>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="px-4 py-6 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 w-full px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors duration-200"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="brand-blue shadow-lg border-b border-blue-300">
          <div className="flex items-center justify-between h-16 px-6">
            
            {/* LEFT SIDE - Mobile menu (mobile only) */}
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-white hover:text-white hover:bg-blue-600 rounded-lg"
              >
                <Menu className="w-4 h-4" />
              </button>
            </div>
            
            {/* CENTER - Company name (both mobile and desktop) */}
            <div className="flex items-center">
              {/* Company Name - Centered on both mobile and desktop */}
              <div className="text-base font-semibold text-white">
                {user?.companyName || (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'Portal')}
              </div>
            </div>
            
            {/* RIGHT SIDE - Search bar (desktop) + Action buttons */}
            <div className="flex items-center space-x-2">
              
              {/* Global Search Bar - Desktop only, moved to right */}
              <div className="hidden lg:flex w-80">
                <div className="relative w-full search-dropdown">
                  <input 
                    type="text"
                    placeholder="Search contacts, invoices, tasks, payments…"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    className="w-full pl-4 pr-10 py-2 bg-white/15 border border-white/25 rounded-lg text-white placeholder-white/60 focus:bg-white focus:text-gray-900 focus:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/50 transition-all duration-200 shadow-md text-sm"
                  />
                  <Search className="absolute right-3 top-2.5 w-4 h-4 text-white/60 pointer-events-none" />
                  
                  {/* Search Results Dropdown */}
                  {searchDropdownOpen && searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 max-h-96 overflow-y-auto">
                      {searchResults.map((result, index) => {
                        const IconComponent = result.icon;
                        return (
                          <button
                            key={`${result.type}-${result.id}-${index}`}
                            onClick={() => handleSearchResultClick(result)}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors duration-200 flex items-center space-x-3"
                          >
                            <IconComponent className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {result.title}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {result.subtitle}
                              </div>
                            </div>
                            <div className="text-xs text-gray-400 capitalize">
                              {result.type.replace('-', ' ')}
                            </div>
                          </button>
                        );
                      })}
                </div>
                  )}
                </div>
              </div>
              
              {/* Plus Button with Dropdown (desktop only) */}
              <div className="hidden lg:block relative plus-dropdown">
                <button 
                  onClick={() => setPlusDropdownOpen(!plusDropdownOpen)}
                  className="p-2 text-white hover:text-white hover:bg-blue-600 rounded-lg transition-all duration-200 hover:scale-105"
                  title="Create New"
                >
                  <Plus className="w-4 h-4" />
                </button>
                
                {/* Plus Dropdown */}
                {plusDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <button 
                      onClick={() => handlePlusAction('invoice')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <FileText className="w-4 h-4" />
                      <span>New Invoice</span>
                    </button>
                    <button 
                      onClick={() => handlePlusAction('contact')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <Users className="w-4 h-4" />
                      <span>New Contact</span>
                    </button>
                    <button 
                      onClick={() => handlePlusAction('task')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <CheckSquare className="w-4 h-4" />
                      <span>New To-Do Task</span>
                    </button>
                  </div>
                )}
              </div>
              
              {/* Notifications (existing) */}
              <div className="relative notification-dropdown">
                <button
                  onClick={() => {
                    console.log('📱 Notification bell clicked, current state:', notificationDropdownOpen);
                    setNotificationDropdownOpen(!notificationDropdownOpen);
                  }}
                  className="p-2 text-white hover:text-white hover:bg-blue-600 rounded-lg transition-all duration-200 hover:scale-105 relative"
                  title="Notifications"
                >
                  <Bell className="w-4 h-4" />
                  {/* Notification badge */}
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-bold shadow-lg animate-pulse">
                      {unreadNotifications > 9 ? '9+' : unreadNotifications}
                    </span>
                  )}
                </button>

                         {/* Notification Dropdown */}
         {notificationDropdownOpen && (
           <div className="absolute top-full right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 max-h-96 overflow-y-auto">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                      <div className="flex items-center space-x-2">
                        {unreadNotifications > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Mark all as read
                          </button>
                        )}
                        {notifications.length > 0 && (
                          <button
                            onClick={clearAllNotifications}
                            className="text-xs text-red-600 hover:text-red-800 font-medium"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Notifications List */}
                    <div className="space-y-1">
                      {notifications.length > 0 ? (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`px-4 py-3 hover:bg-gray-50 transition-colors duration-200 ${
                              !notification.read ? 'bg-blue-50' : ''
                            }`}
                          >
                            <div className="flex items-start space-x-3">
                              {/* Icon */}
                              <div className={`p-2 rounded-lg ${getNotificationColor(notification.type)}`}>
                                {getNotificationIcon(notification.type)}
                              </div>
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between">
                                  <button
                                    onClick={() => handleNotificationClick(notification)}
                                    className="text-left flex-1 min-w-0 pr-2"
                                  >
                                    <p className={`text-sm font-medium ${
                                      !notification.read ? 'text-gray-900' : 'text-gray-700'
                                    }`}>
                                      {notification.title}
                                    </p>
                                    <p className="text-sm text-gray-600 mt-1 overflow-hidden" style={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical'
                                    }}>
                                      {notification.message}
                                    </p>
                                  </button>
                                  <div className="flex flex-col items-end space-y-1 flex-shrink-0">
                                    <span className="text-xs text-gray-500 whitespace-nowrap">
                                      {formatTimeAgo(notification.createdAt)}
                                    </span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        clearNotification(notification.id);
                                      }}
                                      className="text-gray-400 hover:text-red-500 transition-colors"
                                      title="Clear notification"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                                
                                {/* Pro Plan Indicator for Chat */}
                                {notification.type === 'chat' && notification.requiresPro && userSubscription !== 'Pro' && (
                                  <div className="flex items-center space-x-2 mt-2">
                                    <Lock className="w-3 h-3 text-yellow-600" />
                                    <span className="text-xs text-yellow-700 font-medium">
                                      Upgrade to Pro to read
                                    </span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Unread indicator */}
                              {!notification.status && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-8 text-center text-gray-500">
                          <Bell className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm">No notifications</p>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                      <div className="px-4 py-3 border-t border-gray-100">
                        <button
                          onClick={() => setNotificationDropdownOpen(false)}
                          className="w-full text-center text-sm text-gray-600 hover:text-gray-800 font-medium"
                        >
                          Close
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Enhanced Help Icon with Dropdown */}
              <div className="relative help-dropdown">
                <button 
                  onClick={() => setHelpDropdownOpen(!helpDropdownOpen)}
                  className="p-2 text-white hover:text-white hover:bg-blue-600 rounded-lg transition-all duration-200 hover:scale-105"
                  title="Help & Support"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
                
                {/* Help Dropdown */}
                {helpDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <button 
                      onClick={() => {
                        setHelpDropdownOpen(false);
                        // Navigate to help center or open FAQ modal
                        console.log('Help Center clicked');
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                    >
                      Help Center / FAQs
                    </button>
                    <a 
                      href="mailto:receevit@outlook.com"
                      onClick={() => setHelpDropdownOpen(false)}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                    >
                      Contact Support
                    </a>
                    <button 
                      onClick={() => {
                        setHelpDropdownOpen(false);
                        // Navigate to user guide or open guide modal
                        console.log('User Guide clicked');
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                    >
                      User Guide
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Notification Modal */}
        {showNotificationModal && selectedNotification && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${getNotificationColor(selectedNotification.type)}`}>
                    {getNotificationIcon(selectedNotification.type)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedNotification.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {formatTimeAgo(selectedNotification.timestamp)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowNotificationModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <p className="text-gray-700 mb-6">
                  {selectedNotification.message}
                </p>

                {/* Pro Plan Warning for Chat */}
                {selectedNotification.type === 'chat' && selectedNotification.requiresPro && userSubscription !== 'Pro' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center space-x-2">
                      <Lock className="w-5 h-5 text-yellow-600" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800">
                          Pro Plan Required
                        </p>
                        <p className="text-sm text-yellow-700">
                          Chat is available on Pro plan. Upgrade to read your {selectedNotification.unreadCount || unreadMessages} unread message{(selectedNotification.unreadCount || unreadMessages) !== 1 ? 's' : ''}.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => clearNotification(selectedNotification.id)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Clear notification
                  </button>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowNotificationModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Close
                    </button>
                    
                    {selectedNotification.type === 'chat' && userSubscription === 'Pro' && (
                      <button
                        onClick={() => handleNotificationAction(selectedNotification)}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                      >
                        Open Chat
                      </button>
                    )}
                    
                    {selectedNotification.type === 'chat' && selectedNotification.requiresPro && userSubscription !== 'Pro' && (
                      <button
                        onClick={() => handleNotificationAction(selectedNotification)}
                        className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors"
                      >
                        Upgrade to Pro
                      </button>
                    )}
                    

                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notification Preview Modal - Instant pop-up */}
        {notificationPreviewModal.isOpen && notificationPreviewModal.notification && (() => {
          // Extract rejection reason from message if present
          const message = notificationPreviewModal.notification.message;
          const rejectionReasonMatch = message.match(/Reason: (.+)$/);
          const extractedReason = rejectionReasonMatch ? rejectionReasonMatch[1] : null;
          const displayReason = notificationPreviewModal.notification.metadata?.rejectionReason || extractedReason;
          
          // Debug log to see what we're working with
          console.log('🔍 Notification Debug:', {
            hasMetadata: !!notificationPreviewModal.notification.metadata,
            metadataKeys: notificationPreviewModal.notification.metadata ? Object.keys(notificationPreviewModal.notification.metadata) : [],
            rejectionReasonInMetadata: notificationPreviewModal.notification.metadata?.rejectionReason,
            extractedFromMessage: extractedReason,
            finalDisplayReason: displayReason,
            fullMetadata: notificationPreviewModal.notification.metadata
          });
          
          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 animate-fadeIn">
              <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full transform transition-all animate-slideUp">
                {/* Header - Matching top bar blue */}
                <div className="bg-[#1F11ED] px-6 py-4 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                        <Bell className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">
                          {notificationPreviewModal.notification.title}
                        </h3>
                        <p className="text-xs text-white text-opacity-80">
                          {new Date(notificationPreviewModal.notification.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setNotificationPreviewModal({ isOpen: false, notification: null })}
                      className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-all"
                    >
                      <CloseIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Message */}
                  <div className="mb-6">
                    <p className="text-gray-700 text-base leading-relaxed">
                      {notificationPreviewModal.notification.message}
                    </p>
                  </div>

                  {/* Invoice Details if available */}
                  {notificationPreviewModal.notification.metadata && (
                    <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Invoice Details</h4>
                      
                      {notificationPreviewModal.notification.metadata.invoiceNumber && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Invoice Number:</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {notificationPreviewModal.notification.metadata.invoiceNumber}
                          </span>
                        </div>
                      )}
                      
                      {notificationPreviewModal.notification.metadata.amount && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Amount:</span>
                          <span className="text-sm font-bold text-[#1F11ED]">
                            £{parseFloat(notificationPreviewModal.notification.metadata.amount).toFixed(2)}
                          </span>
                        </div>
                      )}
                      
                      {notificationPreviewModal.notification.metadata.senderName && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">From:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {notificationPreviewModal.notification.metadata.senderName}
                          </span>
                        </div>
                      )}

                      {notificationPreviewModal.notification.metadata.receiverName && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">To:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {notificationPreviewModal.notification.metadata.receiverName}
                          </span>
                        </div>
                      )}

                      {displayReason && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <span className="text-sm text-gray-600">Rejection Reason:</span>
                          <p className="text-sm text-red-600 font-medium mt-1">
                            {displayReason}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setNotificationPreviewModal({ isOpen: false, notification: null })}
                      className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200"
                    >
                      Dismiss
                    </button>
                    
                    {notificationPreviewModal.notification.metadata?.invoiceId && (
                      <button
                        onClick={() => handleViewFullInvoice(notificationPreviewModal.notification)}
                        className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-[#1F11ED] hover:bg-blue-700 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        View Full Invoice
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Invoice Preview Modal - Instant full invoice view from notification */}
        {invoicePreviewModal.isOpen && (
          <InvoicePreview
            invoice={invoicePreviewModal.invoice}
            isOpen={invoicePreviewModal.isOpen}
            onClose={() => setInvoicePreviewModal({ isOpen: false, invoice: null, loading: false })}
            onDownloadPDF={(invoice) => handleInvoiceDownload('pdf', invoice, { forcePrintablePreview: true })}
            onDownloadExcel={(invoice) => handleInvoiceDownload('csv', invoice)}
            showDownloadButtons={true}
            title={invoicePreviewModal.loading ? "Loading Invoice..." : "Invoice Preview"}
            loading={invoicePreviewModal.loading}
          />
        )}

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {isInactiveAccount && (
            <div className="mb-6 space-y-3">
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start space-x-3">
                  <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                    <AlertTriangle className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 className="text-base font-semibold text-amber-900">
                      Your subscription is inactive
                    </h2>
                    <p className="text-sm text-amber-800">
                      Reactivate your plan to regain access to invoices, chat, payments, and team features. Until then, you can manage billing from the Subscription tab.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 gap-3">
              <button
                onClick={() => {
                  try {
                    localStorage.setItem('settings_active_tab', 'subscription');
                  } catch (error) {
                    console.debug('Unable to set settings_active_tab in localStorage:', error);
                  }
                  navigate('/portal/settings?tab=subscription');
                }}
                className="inline-flex items-center justify-center rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
              >
                    Reactivate subscription
                  </button>
                  <button
                onClick={() => {
                  try {
                    localStorage.setItem('settings_active_tab', 'subscription');
                  } catch (error) {
                    console.debug('Unable to set settings_active_tab in localStorage:', error);
                  }
                  navigate('/portal/settings?tab=subscription');
                }}
                    className="inline-flex items-center justify-center rounded-lg border border-amber-300 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
                  >
                    Review plan options
                  </button>
                </div>
              </div>
            </div>
          )}
          <Outlet />
          
          {/* Footer - Inside main content area */}
          <footer className="footer-white border-t border-gray-200 py-8 px-6 mt-8 rounded-lg">
            <div className="max-w-7xl mx-auto">
              {/* Top section - Logo and links */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
                {/* Left - Company info */}
                <div className="flex flex-col">
                  <div className="flex items-center space-x-3 mb-3">
                    <ReceevitLogoCompact size={140} forBlueBackground={false} />
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">Receevit®</p>
                  <p className="text-xs text-gray-500">Financial Management Portal</p>
                </div>

                {/* Center - Legal links */}
                <div className="flex flex-col">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Legal</h4>
                  <div className="flex flex-col space-y-2">
                    <Link to="/privacy" className="text-sm text-gray-600 hover:text-blue-600 transition-colors duration-200">
                      Privacy Policy
                    </Link>
                    <Link to="/terms" className="text-sm text-gray-600 hover:text-blue-600 transition-colors duration-200">
                      Terms of Use
                    </Link>
                    <Link to="/cookies" className="text-sm text-gray-600 hover:text-blue-600 transition-colors duration-200">
                      Cookie Policy
                    </Link>
                    <button
                      type="button"
                      onClick={openPreferences}
                      className="text-left text-sm text-gray-600 hover:text-blue-600 transition-colors duration-200"
                    >
                      Cookie Preferences
                    </button>
                  </div>
                </div>

                {/* Right - Support */}
                <div className="flex flex-col">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Support</h4>
                  <div className="flex flex-col space-y-2">
                    <Link to="/support" className="text-sm text-gray-600 hover:text-blue-600 transition-colors duration-200">
                      Support
                    </Link>
                  </div>
                </div>
              </div>

              {/* Bottom section - Copyright */}
              <div className="border-t border-gray-200 pt-6">
                <p className="text-sm text-gray-500 text-center">
                  © {new Date().getFullYear()} Receevit®. All rights reserved.
                </p>
              </div>
            </div>
          </footer>
        </main>
      </div>
    </div>
    </>
  );
};

export default PortalLayout;
